const sectionSelector = '[data-role-platform]';
const tabSelector = '[data-role-name]';
const backgroundSelector = '[data-role-platform-background]';
const foregroundSelector = '[data-role-platform-foreground]';
const descriptionSelector = '[data-role-platform-description]';
const imageSelector = '.role-platform-section__image';
const lineSelector = '[data-role-platform-line]';

const activeTabClassName = 'role-platform-section__role-button--active';
const activeBackgroundClassName = 'role-platform-section__background--active';
const activeForegroundClassName = 'role-platform-section__foreground--active';
const activeDescriptionClassName = 'role-platform-section__description-panel--active';
const readyLineClassName = 'role-platform-section__line-path--ready';
const viewportActiveClassName = 'animations-in-viewport--active';

const lineDrawDuration = 1550;
const lineEraseDuration = 880;
const panelFadeDelay = 140;

const rolePlatformLinePath = [
    'M -125 780.732 C -104.915 683.428 -0.9245 403.976 198.114 330.536',
    'C 446.913 238.735 673.594 458.694 569.849 571.329',
    'C 531.624 593.011 434.287 604.384 350.733 476.42',
    'C 248.751 320.236 279.494 33.0057 703.228 83.6051',
    'C 713.456 84.6239 723.915 86.0423 734.606 87.6643',
    'C 837.748 103.311 913.193 128.168 973.23 158.281',
    'C 1177.2 259.949 1212.66 421.711 1533.49 476.405',
    'C 1960.84 549.258 1707.28 -60.1739 1533.49 46.1725',
    'C 1359.7 152.519 1751.73 611.691 1941.46 700.673',
].join(' ');

const rolePlatformConfigs = {
    leader: {
        color: 'var(--color-accent)',
        pathData: rolePlatformLinePath,
    },
    'finance-team': {
        color: 'var(--color-purple)',
        pathData: rolePlatformLinePath,
    },
    'travel-coordinator': {
        color: 'var(--color-orange)',
        pathData: rolePlatformLinePath,
    },
    employee: {
        color: 'var(--color-primary)',
        pathData: rolePlatformLinePath,
    },
};

const wait = (duration) => new Promise((resolve) => {
    window.setTimeout(resolve, duration);
});

const easeInOutSmooth = (progress) => progress * progress * (3 - 2 * progress);

class SvgPathAnimator {
    constructor(pathElement, duration = lineDrawDuration) {
        this.pathElement = pathElement;
        this.duration = duration;
        this.length = 0;
        this.currentOffset = 0;
        this.animationFrameId = null;
        this.resolveAnimation = null;
        this.hasPreparedPath = false;
    }

    cancelAnimation() {
        if (this.animationFrameId) {
            window.cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        if (this.resolveAnimation) {
            this.resolveAnimation(false);
            this.resolveAnimation = null;
        }
    }

    setDashOffset(offset) {
        this.currentOffset = offset;
        // Keep the rendered offset non-negative for Safari; a full dash cycle
        // preserves the erase direction while currentOffset tracks animation progress.
        const renderedOffset = offset < 0 ? offset + this.length * 2 : offset;

        this.pathElement.style.strokeDashoffset = renderedOffset;
    }

    preparePath({ pathData, color }) {
        this.pathElement.setAttribute('d', pathData);
        this.pathElement.style.stroke = color;

        this.length = this.pathElement.getTotalLength();
        // Explicit dash/gap pairs avoid WebKit odd-length dash normalization bugs.
        this.pathElement.style.strokeDasharray = `${this.length} ${this.length}`;
        this.pathElement.classList.add(readyLineClassName);
        this.hasPreparedPath = true;
    }

    animateDashOffset(from, to, duration) {
        this.cancelAnimation();

        return new Promise((resolve) => {
            const startTime = window.performance.now();

            this.resolveAnimation = resolve;

            const step = (currentTime) => {
                const elapsedTime = currentTime - startTime;
                const progress = Math.min(elapsedTime / duration, 1);
                const easedProgress = easeInOutSmooth(progress);
                const currentOffset = from + (to - from) * easedProgress;

                this.setDashOffset(currentOffset);

                if (progress < 1) {
                    this.animationFrameId = window.requestAnimationFrame(step);
                    return;
                }

                this.animationFrameId = null;
                this.resolveAnimation = null;
                resolve(true);
            };

            this.animationFrameId = window.requestAnimationFrame(step);
        });
    }

    draw({ pathData, color }) {
        this.preparePath({ pathData, color });

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.setDashOffset(0);
            return Promise.resolve(true);
        }

        this.setDashOffset(this.length);
        return this.animateDashOffset(this.length, 0, this.duration);
    }

    erase() {
        if (!this.hasPreparedPath || !this.length) {
            return Promise.resolve(true);
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.setDashOffset(-this.length);
            return Promise.resolve(true);
        }

        return this.animateDashOffset(this.currentOffset, -this.length, lineEraseDuration);
    }
}

const getRoleConfig = (tab) => {
    const name = tab.dataset.roleName;

    return {
        name,
        ...rolePlatformConfigs[name],
    };
};

const setActiveTab = ({ section, tabs }, roleName, color) => {
    section.style.setProperty('--role-platform-active-color', color);

    tabs.forEach((tab) => {
        const isActive = tab.dataset.roleName === roleName;

        tab.classList.toggle(activeTabClassName, isActive);
        tab.setAttribute('aria-selected', String(isActive));
    });
};

const setActivePanels = ({ backgrounds, foregrounds, descriptions }, roleName) => {
    backgrounds.forEach((background) => {
        const isActive = background.dataset.rolePlatformBackground === roleName;

        background.classList.toggle(activeBackgroundClassName, isActive);
        background.setAttribute('aria-hidden', String(!isActive));
    });

    foregrounds.forEach((foreground) => {
        const isActive = foreground.dataset.rolePlatformForeground === roleName;

        foreground.classList.toggle(activeForegroundClassName, isActive);
    });

    descriptions.forEach((description) => {
        const isActive = description.dataset.rolePlatformDescription === roleName;

        description.classList.toggle(activeDescriptionClassName, isActive);
        description.setAttribute('aria-hidden', String(!isActive));
    });
};

const setActiveRole = (roleElements, roleName, color) => {
    setActiveTab(roleElements, roleName, color);
    setActivePanels(roleElements, roleName);
};

const preloadImages = (section) => {
    const images = section.querySelectorAll(imageSelector);

    images.forEach((image) => {
        image.loading = 'eager';
        image.decoding = 'async';

        if (image.complete) {
            return;
        }

        const preloadedImage = new Image();
        preloadedImage.src = image.currentSrc || image.src;
    });
};

const runWhenSectionIsVisible = (section, callback) => {
    if (section.classList.contains(viewportActiveClassName)) {
        callback();
        return;
    }

    const observer = new MutationObserver(() => {
        if (!section.classList.contains(viewportActiveClassName)) {
            return;
        }

        observer.disconnect();
        callback();
    });

    observer.observe(section, {
        attributes: true,
        attributeFilter: ['class'],
    });
};

const getNextTab = (tabs, currentTab, key) => {
    const currentIndex = tabs.indexOf(currentTab);

    if (key === 'Home') {
        return tabs[0];
    }

    if (key === 'End') {
        return tabs[tabs.length - 1];
    }

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
        return tabs[(currentIndex - 1 + tabs.length) % tabs.length];
    }

    if (key === 'ArrowRight' || key === 'ArrowDown') {
        return tabs[(currentIndex + 1) % tabs.length];
    }

    return null;
};

export const initRolePlatformSection = () => {
    const sections = document.querySelectorAll(sectionSelector);

    sections.forEach((section) => {
        const tabs = Array.from(section.querySelectorAll(tabSelector));
        const backgrounds = Array.from(section.querySelectorAll(backgroundSelector));
        const foregrounds = Array.from(section.querySelectorAll(foregroundSelector));
        const descriptions = Array.from(section.querySelectorAll(descriptionSelector));
        const line = section.querySelector(lineSelector);

        if (!tabs.length || !backgrounds.length || !foregrounds.length || !line) {
            return;
        }

        const roleElements = {
            section, tabs, backgrounds, foregrounds, descriptions,
        };
        const pathAnimator = new SvgPathAnimator(line);
        let activeTab = tabs.find((tab) => tab.classList.contains(activeTabClassName)) || tabs[0];
        let transitionId = 0;

        preloadImages(section);

        const isCurrentTransition = (id) => id === transitionId;

        const playLineTransition = async (roleConfig, transition) => {
            pathAnimator.cancelAnimation();

            if (!roleConfig.pathData || !roleConfig.color) {
                return;
            }

            if (pathAnimator.hasPreparedPath) {
                const erased = await pathAnimator.erase();

                if (!erased || !isCurrentTransition(transition)) {
                    return;
                }

                await wait(panelFadeDelay);

                if (!isCurrentTransition(transition)) {
                    return;
                }
            }

            const drawn = await pathAnimator.draw(roleConfig);

            if (!drawn || !isCurrentTransition(transition)) {
                return;
            }
        };

        const drawInitialLine = () => {
            if (pathAnimator.hasPreparedPath) {
                return;
            }

            transitionId += 1;
            playLineTransition(getRoleConfig(activeTab), transitionId);
        };

        const activateRole = (nextTab) => {
            if (nextTab === activeTab) {
                return;
            }

            transitionId += 1;
            activeTab = nextTab;

            const nextConfig = getRoleConfig(nextTab);

            setActiveRole(roleElements, nextConfig.name, nextConfig.color);
            playLineTransition(nextConfig, transitionId);
        };

        const activeConfig = getRoleConfig(activeTab);

        setActiveRole(roleElements, activeConfig.name, activeConfig.color);
        runWhenSectionIsVisible(section, drawInitialLine);

        tabs.forEach((tab) => {
            tab.addEventListener('click', () => {
                activateRole(tab);
            });

            tab.addEventListener('keydown', (event) => {
                const nextTab = getNextTab(tabs, tab, event.key);

                if (!nextTab) {
                    return;
                }

                event.preventDefault();
                nextTab.focus();
                activateRole(nextTab);
            });
        });
    });
};